import { zodResolver } from '@hookform/resolvers/zod';
import {
  can,
  type DeskDto,
  firstName,
  type InviteCreateInput,
  inviteCreateInput,
  type MeDto,
  PERMISSION_TABLE,
  ROLE_META,
  type Role,
  type TeamMemberDto,
} from '@hudhuria/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { desksQuery, qk, teamQuery, useCenter, useMe } from '~/api/queries';
import { DeskIcon, KeyIcon } from '~/components/icons';
import {
  Avatar,
  BottomSheet,
  Chip,
  EmptyState,
  FieldError,
  FieldLabel,
  Overlay,
  OverlayFooter,
  OverlayHeader,
  PrimaryButton,
  SecondaryButton,
  SectionLabel,
  SegmentedControl,
  Spinner,
  TextField,
} from '~/components/ui';
import { api, ok } from '~/lib/api';
import { useBack } from '~/lib/nav';
import { isNative } from '~/lib/platform';
import { toast } from '~/lib/toast';
import { useOnline } from '~/offline/online';

export const Route = createFileRoute('/_app/team')({
  beforeLoad: ({ context }) => {
    const me = context.queryClient.getQueryData<MeDto>(['me']);
    if (!can(me?.staff.role, 'staff')) throw redirect({ to: '/hub' });
  },
  component: Team,
});

const ROLE_COL: Record<Role, number> = { teacher: 0, admin: 1, super: 2 };

function Team() {
  const back = useBack('/me');
  const me = useMe().data;
  const center = useCenter();
  const online = useOnline();
  const team = useQuery(teamQuery);
  const desks = useQuery(desksQuery);
  const [inviting, setInviting] = useState(false);
  const [member, setMember] = useState<TeamMemberDto | null>(null);
  const [desk, setDesk] = useState<DeskDto | null>(null);
  if (!me) return <Overlay>{null}</Overlay>;

  const isSuper = me.staff.role === 'super';
  const col = ROLE_COL[me.staff.role];

  return (
    <Overlay>
      <OverlayHeader
        back="Back"
        onBack={back}
        title="Team & roles"
        subtitle={
          isSuper
            ? `Everyone across all ${me.centers.length} centers`
            : `${center?.name ?? ''} team · you can add teachers`
        }
        pb={10}
      />
      <div className="flex flex-1 flex-col gap-[18px] px-5 pt-2 pb-5 scroll-y">
        <div className="flex shrink-0 flex-col overflow-hidden rounded-2xl border border-line">
          {team.isPending && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}
          {team.isError && (
            <EmptyState>{online ? team.error.message : 'The team list needs a connection.'}</EmptyState>
          )}
          {team.data?.map((m) => {
            const meta = ROLE_META[m.role];
            return (
              <button
                type="button"
                key={m.id}
                onClick={() => setMember(m)}
                className="flex cursor-pointer items-center gap-3 border-0 border-b border-solid border-line-faint bg-white px-[14px] py-3 text-left last:border-b-0"
              >
                <Avatar name={m.name || m.email} size={40} radius="full" fontSize={13} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-semibold">
                    {m.name || m.email}
                    {m.id === me.staff.id && <span className="font-normal text-slate-400"> · you</span>}
                  </div>
                  <div className="mt-px truncate text-[11.5px] text-slate-400">
                    {m.status === 'invited' ? `Invite sent · ${m.email}` : m.detail}
                  </div>
                </div>
                <span
                  className="rounded-[20px] px-[9px] py-1 text-[10.5px] font-bold whitespace-nowrap"
                  style={{ background: meta.bg, color: meta.fg }}
                >
                  {meta.label}
                </span>
              </button>
            );
          })}
        </div>

        <div>
          <SectionLabel className="mb-[9px]">Front-desk devices</SectionLabel>
          <div className="flex shrink-0 flex-col overflow-hidden rounded-2xl border border-line">
            {desks.data?.map((d) => (
              <button
                type="button"
                key={d.id}
                onClick={() => setDesk(d)}
                className="flex cursor-pointer items-center gap-3 border-0 border-b border-solid border-line-faint bg-white px-[14px] py-3 text-left last:border-b-0"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <DeskIcon />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-semibold">{d.name}</div>
                  <div className="mt-px truncate text-[11.5px] text-slate-400">
                    {d.centerName} · {d.lastUsedAt ? `used ${ago(d.lastUsedAt)}` : `set up by ${d.createdBy}`}
                  </div>
                </div>
              </button>
            ))}
            {desks.data?.length === 0 && (
              <div className="px-[14px] py-[13px] text-[12.5px] leading-normal text-slate-500">
                None yet. On the tablet at reception, sign in and turn on <b>Front-desk mode</b> in Me. Staff
                there then sign in by tapping their name and entering their PIN.
              </div>
            )}
            {desks.isError && <EmptyState>The device list needs a connection.</EmptyState>}
          </div>
        </div>

        <div>
          <SectionLabel className="mb-[9px]">What each role can do</SectionLabel>
          <div className="shrink-0 overflow-hidden rounded-2xl border border-line">
            <div className="grid grid-cols-[minmax(0,1fr)_54px_54px_54px] bg-fill-soft px-3 py-[10px] text-center text-[10.5px] font-bold text-slate-500">
              <div className="text-left">PERMISSION</div>
              {['Teacher', 'Admin', 'Super'].map((l, i) => (
                <div key={l} style={{ color: i === col ? '#0369a1' : '#64748b' }}>
                  {l}
                </div>
              ))}
            </div>
            {PERMISSION_TABLE.map((r) => (
              <div
                key={r.label}
                className="grid grid-cols-[minmax(0,1fr)_54px_54px_54px] items-center border-t border-line-faint px-3 py-[9px] text-[12.5px]"
              >
                <div className="text-slate-700">{r.label}</div>
                {r.cells.map((c, i) => {
                  const cell =
                    c === 'yes'
                      ? { m: '✓', bg: '#ecfdf5', fg: '#047857' }
                      : c === 'own'
                        ? { m: 'Own', bg: '#fef3c7', fg: '#b45309' }
                        : { m: '–', bg: 'transparent', fg: '#cbd5e1' };
                  return (
                    <div key={i} className="flex justify-center">
                      <div
                        className="flex h-[22px] min-w-[22px] items-center justify-center rounded-[7px] px-1 text-[11px] font-bold"
                        style={{ background: cell.bg, color: cell.fg }}
                      >
                        {cell.m}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <OverlayFooter>
        <PrimaryButton className="p-[15px] text-[15px]" disabled={!online} onClick={() => setInviting(true)}>
          {isSuper ? 'Invite staff · any role' : 'Invite a teacher'}
        </PrimaryButton>
      </OverlayFooter>

      {inviting && <InviteSheet me={me} onClose={() => setInviting(false)} />}
      {member && <MemberSheet me={me} member={member} onClose={() => setMember(null)} />}
      {desk && <DeskSheet desk={desk} onClose={() => setDesk(null)} />}
    </Overlay>
  );
}

function InviteSheet({ me, onClose }: { me: MeDto; onClose: () => void }) {
  const qc = useQueryClient();
  const isSuper = me.staff.role === 'super';
  const center = useCenter();
  const [link, setLink] = useState<{ url: string; email: string } | null>(null);
  const form = useForm<InviteCreateInput>({
    resolver: zodResolver(inviteCreateInput),
    defaultValues: { email: '', role: 'teacher', centerIds: center ? [center.id] : [] },
  });
  const role = form.watch('role');
  const invite = useMutation({
    mutationFn: (v: InviteCreateInput) =>
      ok<{ member: TeamMemberDto; inviteUrl: string }>(api.team.invites.$post({ json: v })),
    onSuccess: (res, v) => {
      void qc.invalidateQueries({ queryKey: qk.team });
      setLink({ url: res.inviteUrl, email: v.email });
    },
    onError: (e) => form.setError('root', { message: e.message }),
  });

  if (link) {
    return (
      <BottomSheet onClose={onClose}>
        <div className="font-display text-[20px] font-bold">Invite sent</div>
        <div className="mt-1 text-[13.5px] leading-normal text-slate-500">
          We emailed <b className="text-ink">{link.email}</b> a link to set their password and PIN. You can
          also share it directly — it works once, for 7 days.
        </div>
        <div className="mt-4 rounded-xl bg-fill p-3 font-mono text-[12px] break-all text-slate-600">
          {link.url}
        </div>
        <div className="mt-4 flex flex-col gap-[10px]">
          <PrimaryButton onClick={() => void shareLink(link.url)}>Share invite link</PrimaryButton>
          <SecondaryButton onClick={onClose}>Done</SecondaryButton>
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet onClose={onClose}>
      <form
        onSubmit={form.handleSubmit((v) => invite.mutate(v))}
        className="flex flex-col gap-[14px] scroll-y"
        noValidate
      >
        <div className="font-display text-[20px] font-bold">
          {isSuper ? 'Invite staff' : 'Invite a teacher'}
        </div>
        <TextField
          label="Work email"
          type="email"
          inputMode="email"
          autoCapitalize="none"
          placeholder="name@institution.org"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />
        {isSuper && (
          <div>
            <FieldLabel>Role</FieldLabel>
            <Controller
              control={form.control}
              name="role"
              render={({ field }) => (
                <SegmentedControl
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    { value: 'teacher', label: 'Teacher' },
                    { value: 'admin', label: 'Center admin' },
                    { value: 'super', label: 'Super admin' },
                  ]}
                />
              )}
            />
          </div>
        )}
        {isSuper && role !== 'super' && (
          <div>
            <FieldLabel>Centers</FieldLabel>
            <Controller
              control={form.control}
              name="centerIds"
              render={({ field }) => (
                <div className="flex flex-wrap gap-[7px]">
                  {me.centers.map((c) => {
                    const on = field.value.includes(c.id);
                    return (
                      <Chip
                        key={c.id}
                        on={on}
                        onClick={() =>
                          field.onChange(on ? field.value.filter((x) => x !== c.id) : [...field.value, c.id])
                        }
                      >
                        {c.name}
                      </Chip>
                    );
                  })}
                </div>
              )}
            />
          </div>
        )}
        {!isSuper && (
          <div className="text-[12.5px] text-slate-500">They'll join {center?.name} as a teacher.</div>
        )}
        {role === 'super' && (
          <div className="text-[12.5px] text-slate-500">Super admins see every center.</div>
        )}
        <FieldError>{form.formState.errors.root?.message}</FieldError>
        <PrimaryButton type="submit" loading={invite.isPending}>
          Send invite
        </PrimaryButton>
      </form>
    </BottomSheet>
  );
}

async function shareLink(url: string) {
  try {
    if (isNative()) {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title: 'Join Hudhuria', text: 'Your Hudhuria invite', url });
    } else if (navigator.share) {
      await navigator.share({ title: 'Join Hudhuria', url });
    } else {
      await navigator.clipboard.writeText(url);
      toast('Invite link copied');
    }
  } catch {
    /* share cancelled */
  }
}

/** Per-person actions: reset the front-desk PIN, remove. */
function MemberSheet({ me, member, onClose }: { me: MeDto; member: TeamMemberDto; onClose: () => void }) {
  const meta = ROLE_META[member.role];
  const [pin, setPin] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const first = firstName(member.name || member.email);
  const self = member.id === me.staff.id;
  const invited = member.status === 'invited';
  const qc = useQueryClient();

  const remove = useMutation({
    mutationFn: () => ok<void>(api.team[':id'].$delete({ param: { id: member.id } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.team });
      toast(invited ? `Invite to ${member.email} withdrawn` : `${first} removed from the team`);
      onClose();
    },
    onError: (e) => toast(e.message),
  });
  const resetPin = useMutation({
    mutationFn: () => ok<{ pin: string }>(api.team[':id']['reset-pin'].$post({ param: { id: member.id } })),
    onSuccess: (r) => setPin(r.pin),
    onError: (e) => toast(e.message),
  });

  return (
    <BottomSheet onClose={onClose}>
      <div className="flex items-center gap-3">
        <Avatar name={member.name || member.email} size={48} radius="full" fontSize={15} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[19px] font-bold">{member.name || 'Invited'}</div>
          <div className="truncate text-[12.5px] text-slate-500">
            {member.email} · {member.code}
          </div>
        </div>
        <span
          className="rounded-[20px] px-[9px] py-1 text-[10.5px] font-bold whitespace-nowrap"
          style={{ background: meta.bg, color: meta.fg }}
        >
          {meta.label}
        </span>
      </div>
      {member.status === 'invited' && (
        <div className="mt-3 rounded-xl bg-amber-50 px-3 py-[10px] text-[12.5px] text-amber-800">
          Hasn't accepted their invite yet.
        </div>
      )}

      {pin ? (
        <div className="mt-5 rounded-2xl bg-fill-soft p-5 text-center">
          <div className="text-[12.5px] font-semibold text-slate-500">New PIN for {first}</div>
          <div className="mt-1 font-display text-[40px] font-bold tracking-[0.3em] pl-[0.3em]">{pin}</div>
          <div className="mt-1 text-[12px] text-slate-500">
            Shown once — tell {first} privately. Their old PIN no longer works.
          </div>
        </div>
      ) : confirmRemove ? (
        <div className="mt-5">
          <div className="rounded-xl bg-red-50 px-3 py-[10px] text-[13px] leading-normal text-red-800">
            {invited
              ? `The invite link stops working and ${member.email} won't join.`
              : `${first} is signed out on every device and their password and PIN stop working. The check-ins they recorded stay on file.`}
          </div>
          <PrimaryButton
            className="mt-3"
            style={{ background: '#dc2626' }}
            loading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {invited ? 'Withdraw invite' : `Remove ${first}`}
          </PrimaryButton>
          <button
            type="button"
            onClick={() => setConfirmRemove(false)}
            className="mt-2 w-full cursor-pointer border-none bg-transparent py-2 text-[13.5px] font-semibold text-slate-500"
          >
            Keep {invited ? 'the invite' : first}
          </button>
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-[10px]">
          {!self && !invited && (
            <SecondaryButton disabled={resetPin.isPending} onClick={() => resetPin.mutate()}>
              <KeyIcon className="text-brand" />
              {resetPin.isPending ? 'Resetting…' : 'Reset PIN'}
            </SecondaryButton>
          )}
          {!self && (
            <button
              type="button"
              onClick={() => setConfirmRemove(true)}
              className="mt-1 cursor-pointer border-none bg-transparent py-2 text-[13.5px] font-semibold text-red-600"
            >
              {invited ? 'Withdraw invite' : 'Remove from team'}
            </button>
          )}
        </div>
      )}
    </BottomSheet>
  );
}

/** "5 min ago", "yesterday", "12 Mar" for the device list. */
function ago(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  if (hours < 48) return 'yesterday';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** Turn front-desk mode off for a device, e.g. a lost tablet. It stops working straight away. */
function DeskSheet({ desk, onClose }: { desk: DeskDto; onClose: () => void }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => ok<void>(api.desks[':id'].$delete({ param: { id: desk.id } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.desks });
      toast(`${desk.name} is no longer a front desk`);
      onClose();
    },
    onError: (e) => toast(e.message),
  });
  return (
    <BottomSheet onClose={onClose}>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand">
          <DeskIcon size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[19px] font-bold">{desk.name}</div>
          <div className="truncate text-[12.5px] text-slate-500">
            {desk.centerName} · set up by {desk.createdBy}
          </div>
        </div>
      </div>
      <div className="mt-4 rounded-xl bg-fill-soft px-3 py-[10px] text-[13px] leading-normal text-slate-600">
        Staff at {desk.centerName} sign in on this device by tapping their name and entering their PIN. Turn
        it off if the device is lost or no longer at the desk: it stops working straight away and anyone who
        signed in on it with their PIN is signed out.
      </div>
      <PrimaryButton
        className="mt-4"
        style={{ background: '#dc2626' }}
        loading={remove.isPending}
        onClick={() => remove.mutate()}
      >
        Turn off front-desk mode
      </PrimaryButton>
    </BottomSheet>
  );
}
